/**
 * Supplies the three halves of the editor's card-reference auto-detection:
 * the tenant's project codes (the free pre-filter for a TYPED shorthand),
 * the same-origin URL parser (the free pre-filter for a PASTED link), and
 * the resolver that confirms a work item exists before any text is
 * converted.
 *
 * Returns a STABLE object holding getters rather than values, because
 * `<MarkdownEditor>` captures its extension options once at editor-init
 * time. Reading the codes at detection time is what lets the projects
 * query resolve after an editor has already mounted — the alternative
 * (baking the list in) would leave any editor mounted during the first
 * load permanently unable to detect anything.
 */

import { useMemo, useRef } from 'react'

import type { WorkItemAutodetectOptions } from '@/editor/work-item-autodetect'
import { useProjects } from '@/features/projects/use-projects'

import { parseCardDeepLink } from './card-deep-link'
import { useResolveCardReference } from './use-resolve-card-reference'

export function useWorkItemAutodetect(): WorkItemAutodetectOptions {
  const projects = useProjects()
  const resolveReference = useResolveCardReference()

  // Refs, not deps: the returned object must stay referentially stable
  // for the lifetime of the editor, while still reading current values.
  const codesRef = useRef<readonly string[]>([])
  codesRef.current = useMemo(
    () =>
      (projects.data ?? [])
        .map((project) => project.code)
        .filter((code): code is string => typeof code === 'string' && code.length > 0)
        .map((code) => code.toUpperCase()),
    [projects.data],
  )

  const resolveRef = useRef(resolveReference)
  resolveRef.current = resolveReference

  return useMemo(
    () => ({
      getProjectCodes: () => codesRef.current,
      // Bound to the LIVE origin at call time rather than at mount, so a
      // link is judged against the workspace the reader is actually in.
      parseCardUrl: (href: string) => parseCardDeepLink(href, window.location.origin),
      resolveReference: (reference) => resolveRef.current(reference),
    }),
    [],
  )
}
